import React, { useEffect, useState } from "react";
import {
  Box,
  Button,
  Chip,
  Divider,
  IconButton,
  Paper,
  Switch,
  TextField,
  Typography,
  makeStyles,
} from "@material-ui/core";
import { Add, Delete, CheckCircle, Cancel, Settings } from "@material-ui/icons";
import {
  doc,
  getDoc,
  updateDoc,
  collection,
  getDocs,
} from "firebase/firestore";
import { MODE_DEMO, REGLAGES_DEMO } from "../../Utils/demoMode";
import { useFirebase } from "../../Firebase/FirebaseContext";
// Bloc « Nouveauté » en bas de page — idée de Nicolas du 03/09.
import { BlocNouveaute } from "../Elements/Nouveaute";

// Masque de saisie, volontairement lisible :
//   9 -> un chiffre, A -> une lettre, tout autre caractere -> impose tel quel.
// Meme regle que dans l'application tablette (packages/mobile/utils/labSettings).
function correspond(valeur, masque) {
  if (!masque) return true;
  const v = String(valeur || "");
  if (v.length !== masque.length) return false;
  for (let i = 0; i < masque.length; i += 1) {
    const m = masque[i];
    const c = v[i];
    if (m === "9") {
      if (c < "0" || c > "9") return false;
    } else if (m === "A") {
      if (!/[A-Za-z]/.test(c)) return false;
    } else if (c !== m) {
      return false;
    }
  }
  return true;
}

// Traduction du masque en phrase, pour ne pas avoir a comprendre la syntaxe.
function enClair(masque) {
  if (!masque) return "";
  const morceaux = [];
  let i = 0;
  while (i < masque.length) {
    const c = masque[i];
    let n = 1;
    while (masque[i + n] === c) n += 1;
    if (c === "9") morceaux.push(`${n} chiffre${n > 1 ? "s" : ""}`);
    else if (c === "A") morceaux.push(`${n} lettre${n > 1 ? "s" : ""}`);
    else morceaux.push(`« ${masque.slice(i, i + n)} » imposé`);
    i += n;
  }
  return `${masque.length} caractères : ${morceaux.join(", puis ")}`;
}

const useStyles = makeStyles((theme) => ({
  page: { padding: 24, maxWidth: 900, margin: "0 auto" },
  titre: { display: "flex", alignItems: "center", gap: 10, marginBottom: 4 },
  bloc: { padding: 24, marginBottom: 24 },
  ligneChamp: {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    padding: "8px 0",
  },
  aide: { color: "#6B7280", fontSize: 13, marginTop: 4, marginBottom: 16 },
  ligneFormat: { display: "flex", alignItems: "center", gap: 12, marginBottom: 12 },
  apercu: { color: "#374151", fontSize: 13, flex: 1 },
  exemples: { background: "#F9FAFB", padding: 12, borderRadius: 6, marginTop: 8 },
  test: { display: "flex", alignItems: "center", gap: 12, marginTop: 8 },
  ok: { color: "#15803D", display: "flex", alignItems: "center", gap: 6 },
  ko: { color: "#B91C1C", display: "flex", alignItems: "center", gap: 6 },
}));

export default function LabSettings() {
  const classes = useStyles();
  const { firebase } = useFirebase();
  const labId = localStorage.getItem("currentLab");

  const [champs, setChamps] = useState({
    telephone: true,
    email: true,
    dossier: true,
  });
  /*
    Garde-fous de saisie, activables par laboratoire.

    Retour du 02/09 : « pour cette notion de garde fou, peux tu le rendre
    activable par les laboratoires ? »

    Valeur par defaut : actifs. Un garde-fou qu'il faut penser a allumer ne
    sert a rien ; celui qui n'en veut pas fait un geste explicite pour
    l'eteindre.
  */
  const [controles, setControles] = useState({
    dateNaissance: true,
    formatCodeBarres: false,
  });
  const [formats, setFormats] = useState([]);
  const [essai, setEssai] = useState("");
  const [resultatEssai, setResultatEssai] = useState(null);
  const [enregistre, setEnregistre] = useState(false);
  const [chargement, setChargement] = useState(true);
  // Diffusion des controles a tous les laboratoires (retour du 08/09, 16h23).
  const [diffusion, setDiffusion] = useState("repos"); // repos | confirme | encours
  const [resultatDiffusion, setResultatDiffusion] = useState(null);

  useEffect(() => {
    if (!labId && !MODE_DEMO) return;
    (async () => {
      try {
        // En mode demonstration, cet ecran lit une reponse simulee : il
        // interroge Firestore directement, pas l'API, donc l'adaptateur axios
        // ne peut rien pour lui.
        const data = MODE_DEMO
          ? REGLAGES_DEMO
          : (await getDoc(doc(firebase.db, "laboratories", labId))).data() || {};
        const conf = data.champsPatient || {};
        setChamps({
          telephone: conf.telephone !== false,
          email: conf.email !== false,
          dossier: conf.dossier !== false,
        });
        const ctrl = data.controlesSaisie || {};
        setControles({
          dateNaissance: ctrl.dateNaissance !== false,
          // Le defaut a change cote tablette le 08/09 : le controle de
          // format des code-barres est ETEINT sauf si le laboratoire l'allume
          // (mobile/utils/labSettings.js). La case doit dire la meme chose,
          // sans quoi cet ecran afficherait « actif » pour un controle qui ne
          // s'exerce pas — le pire des deux mondes.
          formatCodeBarres: ctrl.formatCodeBarres === true,
        });
        const brut = data.formatCodeBarres;
        setFormats(Array.isArray(brut) ? brut : brut ? [brut] : []);
      } catch (e) {
        console.error("Lecture des réglages du laboratoire :", e);
      } finally {
        setChargement(false);
      }
    })();
  }, [firebase.db, labId]);

  const enregistrer = async () => {
    if (MODE_DEMO) {
      setEnregistre(true);
      setTimeout(() => setEnregistre(false), 3000);
      return;
    }
    try {
      await updateDoc(doc(firebase.db, "laboratories", labId), {
        champsPatient: champs,
        controlesSaisie: controles,
        formatCodeBarres: formats.filter(Boolean),
        // Le depot SFTP n'est volontairement PAS ecrit ici : il est pose cote
        // developpeur. Cet ecran ne doit rien pouvoir en changer.
      });
      setEnregistre(true);
      setTimeout(() => setEnregistre(false), 3000);
    } catch (e) {
      console.error("Enregistrement des réglages :", e);
    }
  };

  /**
   * Applique les CONTROLES DE SAISIE a tous les laboratoires.
   *
   * Retour du 08/09 (16h23) : « maintenant j'aimerais que ce reglage soit
   * diffuse sur tous les laboratoires ». Il venait d'eteindre le controle de
   * format des code-barres pour son propre laboratoire, et voulait le meme
   * etat partout.
   *
   * ─────────────────────────────────────────────────────────────────────────
   * CE QUI SE DIFFUSE, ET SURTOUT CE QUI NE SE DIFFUSE PAS
   * ─────────────────────────────────────────────────────────────────────────
   *
   * SEULS les interrupteurs (`controlesSaisie`) partent. Ils repondent a une
   * question de politique — « previent-on sur ce point, oui ou non ? » — dont
   * la reponse a vocation a etre la meme partout.
   *
   * Les FORMATS de numero de dossier (`formatCodeBarres`) ne partent PAS, et
   * ce n'est pas un oubli : chaque laboratoire a ses propres series
   * d'etiquettes. Les diffuser imposerait a tous celles d'un seul, et ferait
   * crier l'avertissement sur les numeros valides de tous les autres —
   * exactement le defaut qu'on a passe la journee du 08/09 a eteindre.
   *
   * Les champs demandes au patient (`champsPatient`) ne partent pas non plus :
   * un laboratoire qui a choisi de ne pas collecter d'e-mail l'a choisi.
   *
   * On ecrit PAR FUSION (`updateDoc`) : le reste de chaque fiche est intact.
   * Et on compte les echecs plutot que de s'arreter au premier : un
   * laboratoire refuse ne doit pas priver les autres du reglage.
   */
  const diffuserATous = async () => {
    if (MODE_DEMO) {
      setResultatDiffusion({
        ok: true,
        message: "Mode démonstration : rien n'a été écrit.",
      });
      setDiffusion("repos");
      return;
    }
    setDiffusion("encours");
    setResultatDiffusion(null);
    try {
      const labos = await getDocs(collection(firebase.db, "laboratories"));
      let reussis = 0;
      let echecs = 0;
      for (const labo of labos.docs) {
        try {
          await updateDoc(labo.ref, { controlesSaisie: controles });
          reussis += 1;
        } catch (e) {
          echecs += 1;
          console.error("Diffusion des contrôles —", labo.id, e);
        }
      }
      setResultatDiffusion({
        ok: echecs === 0,
        message:
          echecs === 0
            ? `Contrôles appliqués à ${reussis} laboratoire${reussis > 1 ? "s" : ""}.`
            : `${reussis} laboratoire${reussis > 1 ? "s" : ""} mis à jour, ${echecs} en échec (détail dans la console).`,
      });
    } catch (e) {
      console.error("Diffusion des contrôles :", e);
      setResultatDiffusion({
        ok: false,
        message: "La diffusion a échoué : la liste des laboratoires n'a pas pu être lue.",
      });
    } finally {
      setDiffusion("repos");
    }
  };

  const tester = () => {
    const acceptes = formats.filter((f) => f && correspond(essai, f));
    setResultatEssai(
      formats.filter(Boolean).length === 0
        ? { ok: true, message: "Aucun format imposé, tout est accepté." }
        : acceptes.length
        ? { ok: true, message: `Accepté par le format ${acceptes[0]}` }
        : { ok: false, message: "Refusé : ne correspond à aucun format." }
    );
  };

  if (chargement) return <Box className={classes.page}>Chargement…</Box>;

  return (
    <Box className={classes.page}>
      <Box className={classes.titre}>
        <Settings />
        <Typography variant="h5">Réglages du laboratoire</Typography>
      </Box>
      <Typography className={classes.aide}>
        Ces réglages s'appliquent à la tablette, immédiatement, sans mise à jour
        de l'application. Écran accessible depuis le menu de gauche, au-dessus
        de « Secrétariat (dossiers) ».
      </Typography>

      <Paper className={classes.bloc}>
        <Typography variant="h6">Informations demandées au patient</Typography>
        <Typography className={classes.aide}>
          Décochez ce que votre laboratoire ne souhaite pas collecter. Le champ
          disparaît de la tablette, à l'accueil comme en salle de prélèvement.
        </Typography>
        {[
          ["telephone", "Téléphone du patient"],
          ["email", "Adresse e-mail du patient"],
          ["dossier", "Numéro de dossier"],
        ].map(([cle, libelle]) => (
          <Box key={cle} className={classes.ligneChamp}>
            <Typography>{libelle}</Typography>
            <Switch
              color="primary"
              checked={champs[cle]}
              onChange={(e) =>
                setChamps({ ...champs, [cle]: e.target.checked })
              }
            />
          </Box>
        ))}
      </Paper>

      <Paper className={classes.bloc}>
        <Typography variant="h6">Garde-fous de saisie</Typography>
        <Typography className={classes.aide}>
          Ces contrôles <b>préviennent</b> l'hôtesse quand une saisie paraît
          fausse — un mois 14 dans une date de naissance, un numéro de dossier
          hors format. Ils n'empêchent jamais d'enregistrer : devant un patient,
          on ne bloque personne. Laissez-les actifs sauf raison précise.
        </Typography>
        {[
          [
            "dateNaissance",
            "Date de naissance",
            "Signale une date qui ne peut pas exister, ou qui donnerait plus de 130 ans.",
          ],
          [
            "formatCodeBarres",
            "Format du numéro de dossier",
            "Signale un numéro qui ne correspond à aucun des formats réglés ci-dessous.",
          ],
        ].map(([cle, libelle, detail]) => (
          <Box key={cle} className={classes.ligneChamp}>
            <Box>
              <Typography>{libelle}</Typography>
              <Typography className={classes.aide} style={{ marginTop: 2 }}>
                {detail}
              </Typography>
            </Box>
            <Switch
              color="primary"
              checked={controles[cle]}
              onChange={(e) =>
                setControles({ ...controles, [cle]: e.target.checked })
              }
            />
          </Box>
        ))}

        {/* Diffusion a tous les laboratoires — retour du 08/09 (16h23).
            Deux temps volontairement : ce bouton ecrit sur TOUTES les fiches,
            et il n'y a pas de retour en arriere. Un clic seul serait trop peu
            pour une action qui touche tout le monde. */}
        <Divider style={{ margin: "16px 0 12px" }} />
        <Typography className={classes.aide} style={{ marginBottom: 8 }}>
          Ces deux interrupteurs peuvent être appliqués à <b>tous les
          laboratoires</b> d'un coup. Les formats de numéro de dossier
          ci-dessous, eux, restent propres à chaque laboratoire et ne sont
          jamais diffusés.
        </Typography>
        {diffusion !== "confirme" ? (
          <Button
            variant="outlined"
            color="primary"
            disabled={diffusion === "encours"}
            onClick={() => {
              setResultatDiffusion(null);
              setDiffusion("confirme");
            }}
          >
            {diffusion === "encours"
              ? "Diffusion en cours…"
              : "Appliquer ces contrôles à tous les laboratoires"}
          </Button>
        ) : (
          <Box style={{ display: "flex", gap: 8, alignItems: "center", flexWrap: "wrap" }}>
            <Typography style={{ fontWeight: 600 }}>
              Appliquer à tous les laboratoires ? Cette action ne s'annule pas.
            </Typography>
            <Button variant="contained" color="primary" onClick={diffuserATous}>
              Oui, appliquer partout
            </Button>
            <Button onClick={() => setDiffusion("repos")}>Annuler</Button>
          </Box>
        )}
        {resultatDiffusion && (
          <Typography
            style={{
              marginTop: 8,
              fontWeight: 600,
              color: resultatDiffusion.ok ? "#0b6f3b" : "#b3261e",
            }}
          >
            {resultatDiffusion.message}
          </Typography>
        )}
      </Paper>

      <Paper className={classes.bloc}>
        <Typography variant="h6">
          Formats de numéro de dossier acceptés
        </Typography>
        <Typography className={classes.aide}>
          Écrivez un <b>9</b> pour un chiffre, un <b>A</b> pour une lettre, et
          tout autre caractère pour l'imposer tel quel. La longueur du format
          est celle du numéro attendu. Vous pouvez accepter plusieurs formats.
        </Typography>

        <Box className={classes.exemples}>
          <Typography variant="body2">
            <b>99999999</b> : 8 chiffres
          </Typography>
          <Typography variant="body2">
            <b>9999AAA99</b> : 4 chiffres, puis 3 lettres, puis 2 chiffres
          </Typography>
          <Typography variant="body2">
            <b>25999AA99</b> : commence toujours par 25, puis 3 chiffres, 2
            lettres et 2 chiffres
          </Typography>
        </Box>

        <Box mt={3}>
          {formats.length === 0 && (
            <Typography className={classes.aide}>
              Aucun format imposé pour l'instant : tous les numéros sont
              acceptés.
            </Typography>
          )}
          {formats.map((f, i) => (
            <Box key={i} className={classes.ligneFormat}>
              <TextField
                label={`Format ${i + 1}`}
                variant="outlined"
                size="small"
                value={f}
                onChange={(e) => {
                  const copie = [...formats];
                  copie[i] = e.target.value.toUpperCase();
                  setFormats(copie);
                }}
              />
              <Chip size="small" label={`${(f || "").length} caractères`} />
              <Typography className={classes.apercu}>{enClair(f)}</Typography>
              <IconButton
                onClick={() => setFormats(formats.filter((_, j) => j !== i))}
              >
                <Delete />
              </IconButton>
            </Box>
          ))}
          <Button
            startIcon={<Add />}
            color="primary"
            onClick={() => setFormats([...formats, ""])}
          >
            Ajouter un format
          </Button>
        </Box>

        <Divider style={{ margin: "24px 0" }} />

        <Typography variant="subtitle1">Tester un numéro</Typography>
        <Typography className={classes.aide}>
          Saisissez un numéro tel qu'il figure sur vos étiquettes pour vérifier
          qu'il est bien accepté.
        </Typography>
        <Box className={classes.test}>
          <TextField
            variant="outlined"
            size="small"
            placeholder="Numéro à tester"
            value={essai}
            onChange={(e) => {
              setEssai(e.target.value.toUpperCase());
              setResultatEssai(null);
            }}
          />
          <Button variant="outlined" onClick={tester}>
            Tester mon CAB
          </Button>
          {resultatEssai && (
            <Typography
              className={resultatEssai.ok ? classes.ok : classes.ko}
            >
              {resultatEssai.ok ? <CheckCircle /> : <Cancel />}
              {resultatEssai.message}
            </Typography>
          )}
        </Box>

        <Typography className={classes.aide} style={{ marginTop: 20 }}>
          Un numéro hors format n'est jamais bloqué sur la tablette : le
          préleveur est averti et peut passer outre, pour ne pas rester coincé
          devant un patient.
        </Typography>
      </Paper>

      {/* Le depot SFTP ne s'affiche plus ici. Retour du 28/08 : « la partie sur
          le SFTP doit rester du cote developpeur, pas sur les comptes des
          laboratoires. » Le reglage reste en base (champ depotSftp du
          laboratoire) et se pose cote developpeur ; ni le serveur, ni le
          compte, ni le repertoire ne sont exposes au laboratoire, et le test
          de connexion se lance depuis l'API. */}

      <Box display="flex" alignItems="center" style={{ gap: 16 }}>
        <Button variant="contained" color="primary" onClick={enregistrer}>
          Enregistrer les réglages
        </Button>
        {enregistre && (
          <Typography className={classes.ok}>
            <CheckCircle /> Enregistré
          </Typography>
        )}
        <BlocNouveaute section="labSettings" />
      </Box>
    </Box>
  );
}
